
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type GeminiRequestOptions = {
  model?: string;
  systemInstruction?: string;
  responseType?: "text" | "json";
  temperature?: number;
  maxTokens?: number;
  fileData?: { data: string, mimeType: string }[]; // For handling file data
  testDirectApi?: boolean; // New option to test the API directly
};

export type GeminiResponse = {
  result: any;
  generatedText: string;
};

export function useGeminiApi() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<{currentUsage?: number, limit?: number, resetDate?: string} | null>(null);
  const { toast } = useToast();

  const testDirectApi = async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Running direct API test...");
      
      const { data, error: apiError } = await supabase.functions.invoke("gemini-api", {
        body: { testDirectApi: true },
      });
      
      if (apiError) {
        console.error("Direct API test error:", apiError);
        setError(`Direct API test failed: ${apiError.message}`);
        toast({
          title: "API Test Failed",
          description: `Direct API test failed: ${apiError.message}`,
          variant: "destructive",
        });
        return false;
      }
      
      if (data && data.error) {
        console.error("Error from direct API test:", data);
        setError(`Direct API test failed: ${data.error}`);
        
        toast({
          title: "API Test Failed",
          description: data.details?.message || data.error || "Unknown error",
          variant: "destructive",
        });
        return false;
      }
      
      console.log("Direct API test succeeded:", data);
      toast({
        title: "API Test Succeeded",
        description: "The direct API test was successful!",
      });
      return true;
    } catch (err: any) {
      console.error("Error in direct API test:", err);
      setError(err.message || "An unexpected error occurred during the API test");
      toast({
        title: "API Test Error",
        description: "An unexpected error occurred during the API test.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const generateContent = async (
    prompt: string,
    options: GeminiRequestOptions = {}
  ): Promise<GeminiResponse | null> => {
    setIsLoading(true);
    setError(null);
    setQuotaInfo(null);

    // Handle direct API test if requested
    if (options.testDirectApi) {
      const testResult = await testDirectApi();
      if (!testResult) return null;
    }

    try {
      console.log(`Calling Gemini API with ${options.fileData?.length || 0} files`);
      
      // Filter out unsupported file types and log warnings
      const supportedMimeTypes = [
        "image/png",
        "image/jpeg",
        "image/webp",
        "image/heic",
        "image/heif",
        "application/pdf"
      ];
      
      if (options.fileData && options.fileData.length > 0) {
        const unsupportedFiles = options.fileData.filter(
          file => !supportedMimeTypes.includes(file.mimeType)
        );
        
        if (unsupportedFiles.length > 0) {
          console.warn(
            `Warning: ${unsupportedFiles.length} files with unsupported MIME types will be mentioned in the prompt but not processed directly:`, 
            unsupportedFiles.map(f => f.mimeType)
          );
        }
        
        console.log(`Supported file types: ${options.fileData.filter(file => supportedMimeTypes.includes(file.mimeType)).length} of ${options.fileData.length}`);
      }
      
      // Use gemini-2.0-flash as the default model
      const modelToUse = options.model || "gemini-2.0-flash";
      console.log(`Invoking Gemini API with model: ${modelToUse}`);
      
      const { data, error: apiError } = await supabase.functions.invoke("gemini-api", {
        body: {
          prompt,
          model: modelToUse,
          systemInstruction: options.systemInstruction,
          responseType: options.responseType || "text",
          temperature: options.temperature,
          maxTokens: options.maxTokens,
          fileData: options.fileData, // Pass file data if provided
        },
      });

      if (apiError) {
        console.error("Gemini API invocation error:", apiError);
        setError(apiError.message || "Failed to generate content");
        toast({
          title: "Error",
          description: "Failed to generate content. Please try again.",
          variant: "destructive",
        });
        return null;
      }
      
      if (data && data.error) {
        console.error("Error from Gemini API:", data);
        const errorMessage = data.details?.message || data.error;
        setError(errorMessage || "Failed to generate content");
        
        // Show a more helpful message based on the error
        let toastMessage = "Failed to generate content. Please try again.";
        
        // Handle quota exceeded errors specifically
        if (data.error === "Quota exceeded" || (data.message && data.message.includes("quota"))) {
          toastMessage = data.message || "Monthly token quota exceeded. Please try again later.";
          
          // Store quota info for display in the UI
          setQuotaInfo({
            currentUsage: data.currentUsage,
            limit: data.limit,
            resetDate: data.resetDate
          });
        } else if (data.statusCode === 403) {
          toastMessage = "Google API key permissions error or quota limit reached.";
        } else if (data.statusCode === 429) {
          toastMessage = "Rate limit exceeded. Please try again later.";
        } 
        
        toast({
          title: "Error",
          description: toastMessage,
          variant: "destructive",
        });
        return null;
      }

      return data as GeminiResponse;
    } catch (err: any) {
      console.error("Error in useGeminiApi:", err);
      setError(err.message || "An unexpected error occurred");
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generateContent,
    testDirectApi,
    isLoading,
    error,
    quotaInfo
  };
}
